/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var lastTrigger = null;
  var activeDialog = null;
  var restoreTarget = null;
  var focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[role="button"]:not([aria-disabled="true"])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(',');

  function isVisible(element) {
    if (!element) {
      return false;
    }

    var style = window.getComputedStyle(element);
    var rect = element.getBoundingClientRect();

    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && rect.width > 0 && rect.height > 0;
  }

  function getFocusable(dialog) {
    return Array.prototype.slice.call(dialog.querySelectorAll(focusableSelector)).filter(function (element) {
      return isVisible(element) && !element.hasAttribute('inert') && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function patchFrameTitles(root) {
    Array.prototype.forEach.call(root.querySelectorAll('iframe:not([title]), iframe[title=""]'), function (frame) {
      var dialog = frame.closest('[role="dialog"], dialog');
      var label = dialog && (dialog.getAttribute('aria-label') || dialog.getAttribute('aria-labelledby'));

      frame.setAttribute('title', label || 'Embedded video');
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function applyReducedMotion(root) {
    if (!prefersReducedMotion()) {
      return;
    }

    Array.prototype.forEach.call(root.querySelectorAll('video[autoplay]'), function (video) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    });

    Array.prototype.forEach.call(root.querySelectorAll('iframe[src*="autoplay=1"]'), function (frame) {
      frame.src = frame.src.replace(/autoplay=1/g, 'autoplay=0');
    });
  }

  function getVisibleDialogs() {
    return Array.prototype.slice.call(document.querySelectorAll('[role="dialog"], dialog')).filter(isVisible);
  }

  function findCloseControl(dialog, focusable) {
    return focusable.filter(function (element) {
      var label = [
        element.getAttribute('aria-label') || '',
        element.getAttribute('title') || '',
        element.id || '',
        typeof element.className === 'string' ? element.className : '',
        element.textContent || ''
      ].join(' ').toLowerCase();

      return label.indexOf('close') !== -1 || label.indexOf('\u00d7') !== -1 || label.trim() === 'x';
    })[0];
  }

  function focusDialog(dialog) {
    var focusable = getFocusable(dialog);
    var preferred = findCloseControl(dialog, focusable);
    var target = preferred && isVisible(preferred) ? preferred : focusable[0];

    if (!target) {
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      target = dialog;
    }

    target.focus({ preventScroll: true });
  }

  function restoreFocus() {
    var target = restoreTarget || lastTrigger;

    if (target && document.contains(target) && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }

    restoreTarget = null;
  }

  function syncDialogs() {
    patchFrameTitles(document);
    applyReducedMotion(document);

    var visibleDialogs = getVisibleDialogs();
    var nextDialog = visibleDialogs[0] || null;

    if (nextDialog && nextDialog !== activeDialog) {
      activeDialog = nextDialog;
      restoreTarget = lastTrigger && document.contains(lastTrigger) ? lastTrigger : document.activeElement;
      activeDialog.setAttribute('aria-modal', activeDialog.getAttribute('aria-modal') || 'true');
      focusDialog(activeDialog);
    } else if (!nextDialog && activeDialog) {
      activeDialog = null;
      restoreFocus();
    }
  }

  function trapFocus(event) {
    if (!activeDialog || !isVisible(activeDialog)) {
      syncDialogs();
    }

    if (!activeDialog || !isVisible(activeDialog)) {
      return;
    }

    if (event.key === 'Escape') {
      window.setTimeout(syncDialogs, 80);
      window.setTimeout(syncDialogs, 500);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    var focusable = getFocusable(activeDialog);

    if (focusable.length === 0) {
      event.preventDefault();
      activeDialog.focus({ preventScroll: true });
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('button, a, [role="button"]');

    if (trigger) {
      lastTrigger = trigger;
    }

    window.setTimeout(syncDialogs, 0);
    window.setTimeout(syncDialogs, 350);
  }, true);

  document.addEventListener('keydown', function (event) {
    if ((event.key === 'Enter' || event.key === ' ') && event.target && event.target.matches && event.target.matches('button, a, [role="button"]')) {
      lastTrigger = event.target;
    }

    trapFocus(event);
  }, true);

  if (prefersReducedMotion() && window.Element && Element.prototype.scrollIntoView) {
    var scrollIntoView = Element.prototype.scrollIntoView;

    Element.prototype.scrollIntoView = function (options) {
      if (options && typeof options === 'object' && options.behavior === 'smooth') {
        options = Object.assign({}, options, { behavior: 'auto' });
      }

      return scrollIntoView.call(this, options);
    };
  }

  if (window.MutationObserver) {
    new MutationObserver(syncDialogs).observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'open', 'src', 'aria-hidden']
    });
  }

  document.addEventListener('DOMContentLoaded', syncDialogs);
  window.addEventListener('load', syncDialogs);
})();
